/** Format sexual maturity stored in months for display. */
export function formatSexualMaturityMonths(months?: number | null): string {
  if (months == null || months <= 0) return '-';
  if (months >= 12) {
    const years = months / 12;
    const yearLabel = years % 1 === 0 ? `${years}` : years.toFixed(1);
    return `${yearLabel} yr (${months} mo)`;
  }
  return `${months} mo`;
}

/** Show text or dash for empty / placeholder values. */
export function displayText(value?: string | null): string {
  if (value == null) return '-';
  const s = String(value).trim();
  if (!s || s.toLowerCase() === 'unknown' || s.toLowerCase() === 'n/a') return '-';
  return s;
}

export function formatDays(value?: number | null): string {
  if (value == null || value <= 0) return '-';
  return `${value} days`;
}

export function formatYears(value?: number | null): string {
  if (value == null || value <= 0) return '-';
  return `${value} years`;
}

export function formatPriceRange(
  min?: number | null,
  max?: number | null,
  formatCurrency?: (n: number) => string,
): string {
  if (min == null && max == null) return '-';
  if (formatCurrency) {
    if (min != null && max != null) return `${formatCurrency(min)} – ${formatCurrency(max)}`;
    if (min != null) return `from ${formatCurrency(min)}`;
    return `up to ${formatCurrency(max!)}`;
  }
  if (min != null && max != null) return `${min} – ${max}`;
  return String(min ?? max);
}

export function formatAiLastUpdated(species: {
  aiLastUpdated?: string | Date | null;
  lastAiUpdate?: string | null;
}): string {
  const raw = species.aiLastUpdated ?? species.lastAiUpdate;
  if (!raw) return '-';
  try {
    return new Date(raw).toLocaleString();
  } catch {
    return String(raw);
  }
}

export const TAXONOMY_RANKS = [
  { key: 'domain' as const, label: 'Domain' },
  { key: 'kingdom' as const, label: 'Kingdom' },
  { key: 'phylum' as const, label: 'Phylum' },
  { key: 'class' as const, label: 'Class' },
  { key: 'order' as const, label: 'Order' },
  { key: 'family' as const, label: 'Family' },
  { key: 'genus' as const, label: 'Genus' },
  { key: 'species' as const, label: 'Species' },
  { key: 'subspecies' as const, label: 'Subspecies' },
];

/** Resolve incubation days from species (incubationPeriod canonical, hatchingPeriod legacy). */
export function resolveIncubationDays(species?: {
  incubationPeriod?: number | null;
  hatchingPeriod?: number | null;
} | null): number | undefined {
  const incubation = species?.incubationPeriod;
  if (incubation != null && incubation > 0) return incubation;
  const hatching = species?.hatchingPeriod;
  if (hatching != null && hatching > 0) return hatching;
  return undefined;
}

export function formatIncubationDays(species?: {
  incubationPeriod?: number | null;
  hatchingPeriod?: number | null;
} | null): string {
  const days = resolveIncubationDays(species);
  return days != null ? `${days} days` : '-';
}
